import React, { PropsWithChildren } from "react";
import styled, { css } from "styled-components";
import {
  ColumnFiltersState,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
  Row
} from "@tanstack/react-table";
import { darken } from "polished";
import dot from "dot-object";

import { InputCss } from "src/library/components/Forms/Input";
import {
  FilterParams,
  IApiError,
  PaginatedParams,
  formatDate
} from "src/core/shared/api";
import { Icons } from "src/library/components/Icon/types";
import { Icon } from "src/library/components/Icon";
import {
  TableLoading,
  TableNoData
} from "src/library/components/Tables";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";
import { Modal } from "src/library/components/Modal";
import {
  CheckboxLabel,
  CheckboxStyled
} from "src/library/components/Forms";
import { useIsScrollable } from "src/library/hooks/useIsScrollable";
import {
  useEventListener,
  useLocalStorage,
  useWindowSize
} from "src/library/hooks";

import TablePagination from "./TablePagination";
import { ColumnFilter } from "./ColumnFilter";
import { ShadowScroll } from "./ScrollShadow";

export interface TableProps<T> {
  /** What is being displayed in the table in human readable format */
  tableOf: string;
  className?: string;
  /** Table data to view */
  data: T[];
  /** Column definitions - remember to useMemo the columns before passing to the table!! */
  columns: Array<ColumnDef<T, keyof T> & {columnFilterKey?: string}>;
  /** Total pages available */
  pageCount?: number;
  /** Page sizes defaults to [10,20,30]. Passing in an empty array will result in no page size options */
  pageSizeOptions?: number[];
  /** Current row count */
  rowCount?: number;
  /** Function that should be used to trigger a fetch - will be called everytime filters or pagination changes */
  onFetchData: (params: {filters: FilterParams<T>, pagination: PaginatedParams<T>}) => void;
  /** table loading state */
  loading: boolean;
  /** Initial pagination state */
  defaultPagination: PaginatedParams<T>;
  /** Which columns should be visible by default */
  defaultVisibility?: VisibilityState;
  /** Error loaded from an api action to get data for this table */
  error?: IApiError | null;
  /** When in mobile the action that will happen when a row is clicked */
  mainAction?: (info: Row<T>) => void;
  /** Should the header be hidden */
  hideHeader?: boolean;
  /** Filters that will always be used on this table for all requests */
  defaultFilters?: ColumnFiltersState;
}

interface TableSettingState {
  [P: string]: TableSettings;
}

interface TableColumnsAvailable {
  [P: string]: boolean;
}
interface TableSettings {
  columns: TableColumnsAvailable;
}

export interface TableRefProps {
  reloadTable: () => void;
}

const Table = <T, >({
  tableOf,
  className,
  data,
  columns,
  pageCount,
  pageSizeOptions,
  rowCount,
  onFetchData,
  loading,
  defaultPagination,
  defaultVisibility,
  error,
  mainAction,
  hideHeader,
  defaultFilters
}: PropsWithChildren<TableProps<T>>, ref?: React.Ref<TableRefProps>) => {
  const tableRef = React.useRef<HTMLDivElement | null>(null);
  const stickyColumnRef = React.useRef<HTMLTableHeaderCellElement | null>(null);
  const [ stickyColumnWidth, setStickyColumnWidth ] = React.useState(0);
  const { isScrollableLeft, isScrollableRight } = useIsScrollable(tableRef);
  const { width } = useWindowSize();
  const [ tableWidth, setTableWidth ] = React.useState(0);

  const [ pagination, setPagination ] = React.useState<PaginationState>({
    pageIndex: defaultPagination.page,
    pageSize: defaultPagination.limit
  });

  const [ sorting, setSorting ] = React.useState<SortingState>([
    {
      id: defaultPagination.sort[ 0 ][ 0 ].toString(),
      desc: defaultPagination.sort[ 0 ][ 1 ] === "DESC" ? true : false
    }
  ]);

  pageSizeOptions = pageSizeOptions ? pageSizeOptions : [
    10,
    20,
    30,
    50,
    100
  ];

  /** used to store preferences on whate columsn are visible */
  const [ showTablePreferences, setShowTablePreferences ] = React.useState<boolean>(false);
  const [ savedTableSettings, saveTableSettings ] = useLocalStorage("table-options");
  const tableSettingsState: TableSettingState = React.useMemo(() => savedTableSettings.length ? JSON.parse(savedTableSettings) : {}, [ savedTableSettings ]);
  const [ columnVisibility, setColumnVisibility ] = React.useState(defaultVisibility || {});

  const [ columnFilters, setColumnFilters ] = React.useState<ColumnFiltersState>(
    defaultFilters || []
  );

  /** dynamic state variable for the currently loaded table filter */
  const filters = React.useMemo(() => {
    return columnFilters.reduce((filterParams, filter) => {
      const column = columns.find(column => filter.id === column.id);

      if (column) {
        // handle filters by data type
        switch (column.meta?.filter?.filterType) {
          case "dateRange":
            filterParams[ (column.meta?.filter?.filterKeyOverride ?? column.id) as string ] = { $between: [ formatDate((filter.value as {from: string, to: string}).from, "YYYY-MM-DD"), formatDate((filter.value as {from: string, to: string}).to, "YYYY-MM-DD") ] };
            break;
          case "string":
            // in this case just pass the filter straight to the backend
            if (column.meta?.filter?.filterOperation === "none") {
              filterParams[ (column.meta?.filter?.filterKeyOverride ?? column.id) as string ] = `${filter.value}`;
            } else {
              // by default use the $like operator
              filterParams[ (column.meta?.filter?.filterKeyOverride ?? column.id) as string ] = { $like: `%${filter.value}%` };
            }
            break;
          case "select":
            filterParams[ column.id as string ] = filter.value;
        }
      }

      dot.object(filterParams);

      return filterParams;
    }, {});
  }, [ columnFilters, columns ]);

  React.useEffect(() => {
    // set the initail column settings from local storage
    const tableSettings = tableSettingsState[ tableOf ];

    if (tableSettings) {
      setColumnVisibility(tableSettings.columns);
    }
  }, [
    savedTableSettings,
    tableOf,
    tableSettingsState
  ]);

  /** Fetch data on mount */
  React.useEffect(() => {
    onFetchData({
      filters,
      pagination: {
        offset: pagination.pageIndex * pagination.pageSize,
        limit: pagination.pageSize,
        page: pagination.pageIndex,
        sort: [ [ sorting[ 0 ].id, sorting[ 0 ].desc ? "DESC" : "ASC" ] ]
      }
    });
  }, [
    pagination,
    sorting,
    onFetchData,
    filters
  ]);

  /** dynamically set the position of the hover area that triggers the table to scroll right */
  const setStickyHoverRightPosition = () => {
    if (stickyColumnRef.current) {
      const bounds = stickyColumnRef.current.getBoundingClientRect();

      setStickyColumnWidth(bounds.width);
    }

    if (tableRef.current) {
      const bounds = tableRef.current.getBoundingClientRect();

      setTableWidth(bounds.width);
    }
  };

  useEventListener("resize", setStickyHoverRightPosition);

  useEventListener("mouseover", () => {
    setStickyHoverRightPosition();
  }, tableRef.current);

  React.useEffect(() => {
    setTimeout(setStickyHoverRightPosition, 200);
  }, [ data ]);

  /**
   * Responsible for setting column visibility state as well as storing it to local storage by table of name
   */
  const handleColumnVisibilityChange = React.useCallback((visibilityState: VisibilityState) => {
    setColumnVisibility(old => {
      const newState = {
        ...old,
        ...visibilityState
      };

      if (tableSettingsState[ tableOf ]) {
        tableSettingsState[ tableOf ].columns = newState;
      } else {
        tableSettingsState[ tableOf ] = { columns: newState };
      }

      saveTableSettings(JSON.stringify(tableSettingsState));

      return newState;
    });
  }, [
    saveTableSettings,
    tableOf,
    tableSettingsState
  ]);

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination,
      sorting,
      columnVisibility,
      columnFilters
    },
    onPaginationChange: setPagination,
    onColumnVisibilityChange: updater => {
      if (typeof updater !== "function") {
        handleColumnVisibilityChange(updater);
      } else {
        handleColumnVisibilityChange(updater(columnVisibility));
      }
    },
    onColumnFiltersChange: updater => {
      setPagination(curr => ({
        pageSize: curr.pageSize,
        pageIndex: 0
      }));

      if (typeof updater !== "function") {
        setColumnFilters(updater);
      } else {
        setColumnFilters(updater(columnFilters));
      }
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    enableSortingRemoval: false,
    enableHiding: true,
    columnResizeMode: "onChange",
    enableColumnResizing: true
  });

  /** Used to reload the table at any time */
  const reloadTable = React.useCallback(() => onFetchData({
    filters,
    pagination: {
      offset: pagination.pageIndex * pagination.pageSize,
      limit: pagination.pageSize,
      page: pagination.pageIndex,
      sort: [ [ sorting[ 0 ].id, sorting[ 0 ].desc ? "DESC" : "ASC" ] ]
    }
  }), [
    filters,
    onFetchData,
    pagination.pageIndex,
    pagination.pageSize,
    sorting
  ]);

  /** Allows functions to be used in the parent components by ref */
  React.useImperativeHandle(ref, () => {
    return { reloadTable };
  });

  /** Clear the table filters */
  const clearColumnFilters = React.useCallback(() => {
    setColumnFilters([]);

    setPagination(curr => ({
      pageSize: curr.pageSize,
      pageIndex: 0
    }));
  }, []);

  return (
    <>
      {!hideHeader && (
        <StyledTableHeaderWrapper>
          {tableOf && (
            <Text
              fontSize="h2"
              weight="bold"
            >
              {tableOf}
            </Text>
          )}

          <StyledHeaderButtonWrapper>
            <Button
              tooltip="Clear filters"
              onClick={() => {
                clearColumnFilters();
              }}
              disabled={!columnFilters.length}
            >
              <Icon
                icon={Icons.CLOSECIRCLE}
                $fillColor="white"
              />
            </Button>

            <Button
              className="refresh-button"
              loading={loading}
              tooltip="Refresh"
              onClick={() => {
                onFetchData({
                  filters,
                  pagination: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    page: pagination.pageIndex,
                    sort: [ [ sorting[ 0 ].id, sorting[ 0 ].desc ? "DESC" : "ASC" ] ]
                  }
                });
              }}
            >
              <Icon
                icon={Icons.REFRESH}
                $fillColor="white"
              />
            </Button>

            <Button
              onClick={() => setShowTablePreferences(true)}
              tooltip="Show/hide columns"
            >
              <Icon
                icon={Icons.COLUMNS}
                $fillColor="white"
              />
            </Button>
          </StyledHeaderButtonWrapper>

        </StyledTableHeaderWrapper>
      )}

      <div style={{ position: "relative" }}>

        {isScrollableLeft && (
          <ShadowScroll
            direction="left"
            tableRef={tableRef}
          />
        )}

        {isScrollableRight && (
          <ShadowScroll
            direction="right"
            tableRef={tableRef}
            right={stickyColumnWidth || 0}
          />
        )}

        <StyledTableWrapper
          tableWidth={tableWidth}
          ref={tableRef}
          isScrollable = {isScrollableRight || isScrollableLeft}
          id="table"
        >
          <table>
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <>

                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        className={`${header.column.getIsSorted()} ${header.column.columnDef.id === "actions" ? "sticky stick-header" : ""}`}
                        ref={header.column.columnDef.id === "actions" ? stickyColumnRef : null}
                        align={(header.column.columnDef.meta as any)?.style?.align || "center"}
                        style={{
                          position: "relative",
                          width: header.getSize(),
                          flexGrow: header.column.columnDef.size ? 0 : 1
                        }}
                      >

                        {header.isPlaceholder ? null : (
                          <div>
                            <div
                              className={`table-header ${header.column.columnDef.meta?.style?.align} ${header.column.getCanSort() ? "sortable" : ""}`}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              <Text weight="bold">
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </Text>

                              {{
                                asc: <Icon
                                  icon={Icons.ORDERASC}
                                  $fillColor="black"
                                     />,
                                desc: <Icon
                                  icon={Icons.ORDERDESC}
                                  $fillColor="black"
                                      />
                              }[ header.column.getIsSorted() as string ] ?? null}
                            </div>

                            {header.column.getCanFilter() && header.column.columnDef.meta?.filter?.filterType ? (
                              <div className={header.column.columnDef.meta?.style?.align}>
                                <ColumnFilter
                                  column={header.column}
                                  filterType={header.column.columnDef.meta.filter.filterType}
                                  options={header.column.columnDef.meta?.filter?.options}
                                  filter={header.column.getFilterValue() as string}
                                  minLength={header.column.columnDef.meta?.filter?.minLength}
                                />
                              </div>
                            ) : null}

                            {header.column.getCanResize() && (
                              <div
                                className={header.column.getIsResizing() ? "resizer active" : "resizer"}
                                onMouseDown={header.getResizeHandler()}
                                onMouseUp={() => setStickyHoverRightPosition()}
                                onTouchStart={header.getResizeHandler()}
                              />
                            )}
                          </div>
                        )}
                      </th>
                    </>
                  ))}
                </tr>
              ))}
            </thead>

            <tbody>
              {loading && (
                <tr className="table-loading-row">
                  <TableLoading
                    loading={loading}
                    loadingText=""
                  />
                </tr>
              )}

              {!loading && data.length === 0 && (
                <tr className="table-no-data-row">
                  <TableNoData
                    error={error}
                    onRetry={reloadTable}
                  />
                </tr>
              )}

              {table.getRowModel().rows.map(row => (
                <tr
                  key={row.id}
                  onClick={() => {
                    if (width && width < 960) {
                      mainAction ? mainAction(row) : null;
                    }
                  }}
                >
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={cell.column.columnDef.id === "actions" ? "sticky" : ""}
                      style={{
                        width: cell.column.getSize(),
                        flexGrow: cell.column.columnDef.size ? 0 : 1
                      }}
                      align={(cell.column.columnDef.meta as any)?.style?.align || "center"}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </StyledTableWrapper>
      </div>

      {!!pageCount && !!rowCount && rowCount > 0 && (
        <TablePagination
          className={className}
          page={table.getState().pagination.pageIndex}
          pages={pageCount}
          pageSize={table.getState().pagination.pageSize}
          pageSizeOptions={pageSizeOptions.length ? pageSizeOptions : undefined}
          totalRows={rowCount}
          nextText="next"
          previousText="prev"
          onPageChange={table.setPageIndex}
          onPageSizeChange={table.setPageSize}
        />
      )}

      <Modal
        small
        closeIcon
        show={showTablePreferences}
        title={`${tableOf} table preferences`}
        backgroundAction={() => setShowTablePreferences(false)}
        content={(
          <>
            {table.getAllColumns().map(column => {
              return (
                <CheckboxLabel
                  className="checkbox-label"
                  for={`column-${column.id}`}
                  label={typeof column.columnDef.header === "string" ? column.columnDef.header : column.id}
                  key={`col-checkbox-${column.id}`}
                >
                  <CheckboxStyled
                    name={`column-${column.id}`}
                    id={`column-${column.id}`}
                    checked={column.getIsVisible()}
                    onChange={column.getToggleVisibilityHandler()}
                  />
                </CheckboxLabel>
              );
            })}
          </>
        )}
      />

    </>
  );
};

const StyledTableHeaderWrapper = styled.div`${({ theme }) => css`
  display: flex;
  padding: ${theme.spacing.padding.md};
  background-color: ${theme.colors.white};
  justify-content: space-between;
  align-items: center;
`}`;

const StyledHeaderButtonWrapper = styled.div`${({ theme }) => css`
  display: flex;
  justify-content: center;
  align-items: center;

  div {
    margin: ${theme.spacing.margins.xs};
  }
  
  .refresh-button {
    > div {
      margin: 0;
    }
  }
`}`;

const StyledTableWrapper = styled.div<{ isScrollable: boolean; tableWidth: number; }>`${({ theme, tableWidth }) => css`
  display: flex;
  position: relative;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  background-color: ${theme.colors.white};
  

  ::-webkit-scrollbar {
    height: 10px;
  }

  ::-webkit-scrollbar-track {
    border-width: 1px;
    background-color: ${theme.colors.lightGrey};
  }
  
  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.primaryDark};
  }

  th, td {
    padding: ${theme.spacing.padding.sm};
  }

  table td {
    border-bottom: 1px solid ${theme.colors.ziggurat};
    border-left: 1px solid ${theme.colors.ziggurat};
    
    :last-child {
      border-right: 1px solid ${theme.colors.ziggurat};
    }
  }


  table {
    width: 100%;
    border-collapse: separate;
  }

  tr {
    display: inline-flex;
    flex-grow: 1;
  }
  
  tbody {
    display: flex;
    flex-direction: column;
  }
  
  thead {
    display: flex;
  }
  
  th {
    background-color: ${theme.colors.ziggurat};
    border-left: 1px solid ${theme.colors.lightGrey};
    height: 80px;

    :first-child {
      border-left: none;
    }

    &.desc, &.asc {
      background-color: ${darken(0.05, theme.colors.ziggurat)};
    }

    div{
      display: flex;
      flex-direction: column;
      align-items: center;
      align-self: center;

      &.table-header {
        flex-direction: row;
      }

      &.sortable {
        cursor: pointer;
      }
    }

    span {
      margin: ${theme.spacing.margins.xs} 0px;
    }
  }

  td {
    vertical-align: middle;
  }

  .table-loading-row {
    height: 170px;
  }

  .table-no-data-row {
    height: 240px;
    position: relative;
    width: ${tableWidth}px;
  }

  .right {
    margin-left: auto; 
    margin-right: 0;
  }

  .left {
    margin-left: 0; 
    margin-right: auto;
  }

  .resizer {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 5px;
    cursor: col-resize;
    user-select: none;
    touch-action: none;

    &.active, :hover {
      background: ${theme.colors.primaryDark};
      transform: translate(3px);
      z-index: 2;
    }
  }

  .filter-input, .picker-input {
    width: 100%;
    max-width: 300px;
    ${InputCss};
    height: 36px;
  }

  .sticky {
    position: sticky !important;
    right: 0;
    top: 0;
    z-index: 1;
    background-color: ${theme.colors.white};
    display: none;

    @media only screen and (min-width: 960px) {
      display: table-cell;
    }

    button {
      transition: width none !important;
    }

    &.stick-header {
      background-color: ${theme.colors.ziggurat};
    }
  }
`}`;

export default React.forwardRef(Table) as
  <T, >(p: TableProps<T> & { ref?: React.Ref<TableRefProps> }) => React.ReactElement;

// export default React.forwardRef(Table);
